<template>
  <v-row>
    <v-col class="py-0" cols="12">
      <div style="position:relative">
        <v-form ref="form" v-model="valid" @submit.prevent="saveInformationMessage" lazy-validation>
          <v-card :loading="saving">
            <v-card-title>
              <v-icon v-if="informationMessage.__hasChanges" color="primary">mdi-content-save-alert-outline</v-icon>
              <v-icon v-if="!informationMessage.__hasChanges">mdi-information</v-icon>
              <span class="pl-2 headline">
                Information
                <span class="pl-2 subtitle-1">
                  <TimeoutMessage v-model="saving">
                    <span class="primary--text">Saving...</span>
                  </TimeoutMessage>
                </span>
              </span>
            </v-card-title>
            <v-card-text>
              <v-alert dense type="warning" v-if="errors.server">{{errors.server}}</v-alert>
              <v-container>
                <v-row align="end">
                  <v-col cols="12" md="6" style="position:relative;">
                    <v-text-field
                      label="Name*"
                      hint="A short name used to identify and hint to the content of the message"
                      v-model="informationMessage.name"
                      :rules="nameRules"
                      prepend-icon="mdi-sticker-emoji"
                      @click:prepend="setEmoji = !setEmoji"
                      validate-on-blur
                      required
                    ></v-text-field>
                    <emoji :show.sync="setEmoji" :prepend-to.sync="informationMessage.name"></emoji>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="informationMessage.groups"
                      :items="groups"
                      item-text="title"
                      item-value="group_tg_id"
                      attach
                      chips
                      label="Group/s"
                      validate-on-blur
                      multiple
                    ></v-select>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      label="Description"
                      v-model="informationMessage.description"
                      validate-on-blur
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      label="URL Slug"
                      :prefix="`https://t.me/${selectedBot.username}?start=info-`"
                      :rules="slugRules"
                      v-model="informationMessage.slug"
                      validate-on-blur
                    ></v-text-field>
                    <a 
                      v-if="informationMessage.slug && informationMessage.slug.length" 
                      :href="`https://t.me/${selectedBot.username}?start=info-${informationMessage.slug}`">
                      {{`https://t.me/${selectedBot.username}?start=info-${informationMessage.slug}`}}
                    </a>
                  </v-col>
                  <v-col cols="12">
                    <Editor v-model="informationMessage.message" :id="informationMessage.id"></Editor>
                  </v-col>
                  <v-col cols="12">
                    <v-checkbox
                      v-model="informationMessage.members_only"
                      label="Members Only"
                      :hint="informationMessage.members_only ? 'Only members (users that have registered with the bot) can view this information message.'
                      :'Anyone in the group can view this information message.'"
                      persistent-hint
                    ></v-checkbox>
                    <v-radio-group
                      v-if="informationMessage.members_only"
                      v-model="informationMessage.properties.which_members"
                      mandatory
                    >
                      <v-radio
                        label="All Members"
                        value="all_members"
                      ></v-radio>
                      <v-radio
                        label="Verified Members"
                        value="verified_members"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-container>
              <small>*indicates required field</small>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" class="ma-2" outlined type="submit" :loading="saving" v-if="!informationMessage.id">Create Message</v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Emoji from "../widget/Emoji";
import Editor from "../widget/Editor";
import TimeoutMessage from "../widget/TimeoutMessage";

export default {
  data: () => ({
    saving: false,
    hasChanges: false,
    valid: false,
    setEmoji: false,
    nameEmoji: null,
    errors: {
      email: "",
      server: "",
    },
    error: {},
    messageRules: [
      (v) => (v && !!v.length) || "Information message is required",
    ],
    slugRegex: /^[a-z0-9]+(?:_[a-z0-9]+)*$/
  }),
  mounted() {
    this.debouncedSave = this.$debounce(this.saveInformationMessage, 3000);
  },
  computed: {
    ...mapGetters("emoji", ["emojiRegex"]),
    ...mapGetters("app", ["selectedBot"]),
    nameRules() {
      return [
        (v) => !!v || "Information message name is required",
        (v) => {
          return (
            (v.match(this.emojiRegex) && v.split(" ").length <= 4) ||
            (!v.match(this.emojiRegex) && v.split(" ").length <= 3) ||
            "Please don't use more than 3 words"
          );
        },
      ];
    },
    slugRules() {
      return [
        v => !v || (v && v.length <= 50) || 'Slug can be a maximum of 50 characters long',
        v => !v || (v && v.length && this.slugRegex.test(v)) || 'Slug only allows letters, numbers and _ (underscore)',
      ];
    },
    config() {
      return {
        theme: this.$vuetify.theme.dark ? "dark" : "gray",
      };
    },
  },
  methods: {
    ...mapActions("app", ["showMessage"]),
    validate() {
      return this.$refs.form.validate();
    },
    resetValidation() {
      this.$refs.form.resetValidation();
      this.errors.server = "";
    },
    async saveInformationMessage(auto, informationMessage) {
      if (!this.validate()) {
        if (!auto) this.showMessage({ text: "You missed something!", color: "red" });
        return;
      }

      this.saving = true;
      try {
        if (informationMessage) {
          await informationMessage.save();
          informationMessage.__hasChanges = false;
        }
        else await this.informationMessage.save();
        //this.showMessage({ text: "Saved!", color: "green" });
      } catch (error) {
        this.error = error;
        this.showMessage({
          text: error.message || "Error occured!",
          color: "error",
        });
      }
      this.saving = false;
    },
    selectEmoji(emoji) {
      this.informationMessage.name = `${emoji.data} ${this.informationMessage.name}`;
    },
  },
  watch: {
    informationMessage: {
      handler(now, before) {
        if (now.id && now.id !== before.id) {
          if (before.id && before.__hasChanges) {
            this.debouncedSave(true, before);
          }
        } else if (now.id) {
          now.__hasChanges = true;
          this.debouncedSave(true, now);
        }

        if (!this.valid) {
          this.resetValidation();
        }
      },
      deep: true,
    },
    "$vuetify.theme.dark"(isDark) {
      this.config.theme = isDark ? "dark" : "gray";
    },
  },
  components: {
    Emoji,
    Editor,
    TimeoutMessage
  },
  props: ["informationMessage", "groups"],
};
</script>

<style lang="scss">
.fr-box {
  &.fr-basic {
    border: none;
    border-radius: 0;

    .fr-wrapper {
      .fr-element {
        cursor: text;
      }
    }

    .fr-toolbar {
      border: none;
      border-radius: 0;
    }

    .second-toolbar {
      display: none;
    }
  }
}
</style>